import type { ReactNode } from 'react';
import type { MessageInstance } from 'antd/es/message/interface';
import type { DashboardWidgetType } from './types';

/** One notice at a time: a second switch replaces the first, not stacks on it. */
export const TYPE_CHANGE_UNDO_KEY = 'dashboard-type-change-undo';
/** Long enough to read it and reach the button; short enough not to linger. */
const TYPE_CHANGE_UNDO_SECONDS = 6;

export interface TypeChangeUndoOffer {
  message: MessageInstance;
  widgetId: string;
  from: DashboardWidgetType;
  to: DashboardWidgetType;
  /**
   * Takes the widget back; expected to do nothing if it no longer has `to`.
   * Called after the offer was made, so it must be stable — the context's
   * `revertWidgetType` is (a functional `setDraft` with no dependencies).
   */
  revert: (widgetId: string, from: DashboardWidgetType, to: DashboardWidgetType) => void;
  content: ReactNode;
}

interface Pending {
  offer: TypeChangeUndoOffer;
  stopListening: () => void;
}

let pending: Pending | null = null;

/**
 * Whether ⌘Z / Ctrl+Z belongs to a text field rather than to the dashboard: a
 * title being typed keeps its own native undo.
 */
const isTextTarget = (target: EventTarget | null): boolean =>
  target instanceof HTMLElement &&
  (target.isContentEditable || Boolean(target.closest('input, textarea, [contenteditable]')));

/** ⌘Z or Ctrl+Z alone — not ⇧⌘Z (redo), not ⌥Z. */
const isUndoShortcut = (event: KeyboardEvent): boolean =>
  (event.metaKey || event.ctrlKey) &&
  !event.shiftKey &&
  !event.altKey &&
  event.key.toLowerCase() === 'z';

/** Forgets the offer and its shortcut, without touching the notice. */
const release = (): Pending | null => {
  const last = pending;
  pending = null;
  last?.stopListening();
  return last;
};

/**
 * Takes back the last type change on offer (SL-1794), from its notice's button
 * or the shortcut. A no-op once the notice is gone.
 */
export function undoTypeChange(): void {
  const last = release();
  if (!last) return;
  const { message, widgetId, from, to, revert } = last.offer;
  message.destroy(TYPE_CHANGE_UNDO_KEY);
  revert(widgetId, from, to);
}

/** Closes the notice and drops the offer: the editor is going away. */
export function dismissTypeChangeUndo(): void {
  release()?.offer.message.destroy(TYPE_CHANGE_UNDO_KEY);
}

/**
 * Offers to undo a type change for as long as its notice shows.
 *
 * Module state and a window listener, not a component's: the notice is global
 * (AntD `message`) and outlives the settings panel, which unmounts when the
 * widget is deselected or the aside collapses. Tying the shortcut to the panel
 * left a notice whose button worked and whose ⌘Z did not. The listener lives
 * exactly as long as the notice; `DashboardDraftProvider` dismisses both when
 * the editor unmounts.
 *
 * One offer at a time, which assumes one editor at a time — the app mounts a
 * single `DashboardDraftProvider` (router). A second editor mounted alongside
 * would share the slot, and closing either would dismiss the other's offer.
 */
export function offerTypeChangeUndo(offer: TypeChangeUndoOffer): void {
  release();

  const onKeyDown = (event: KeyboardEvent) => {
    if (!isUndoShortcut(event) || isTextTarget(event.target)) return;
    event.preventDefault();
    undoTypeChange();
  };
  window.addEventListener('keydown', onKeyDown);
  const current: Pending = {
    offer,
    stopListening: () => window.removeEventListener('keydown', onKeyDown),
  };
  pending = current;

  void offer.message.open({
    key: TYPE_CHANGE_UNDO_KEY,
    type: 'success',
    duration: TYPE_CHANGE_UNDO_SECONDS,
    // Only this offer's own close releases it: a replaced notice closing late
    // must not cancel the one that replaced it.
    onClose: () => {
      if (pending === current) release();
    },
    content: offer.content,
  });
}
